import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';
import { useWorldStore } from '../state/worldStore';
import { MODEL_URL } from './CityModel';

// PlacedBuildings — copies of the city's own buildings, dropped on the grid.
//
// The brush is a MESH NAME, not an asset. You ?pick=1 a building you like and
// every paste clones that mesh out of the model already in memory, so the
// palette is the whole set with no new art, no new download, and no chance of a
// pasted building looking like it came from somewhere else.
//
// Stored as world_objects rows (kind 'building') carrying BOTH grid_x/grid_y and
// full xyz: the grid is what you edit in, the xyz is what renders. Keeping both
// is why a re-calibrated grid doesn't move buildings that are already placed.
export default function PlacedBuildings() {
  const { scene: city } = useGLTF(MODEL_URL);
  const objects = useWorldStore((s) => s.worldObjects);
  const fit = useWorldStore((s) => s.cityFit);
  const sel = useWorldStore((s) => s.worldObjectSel);
  // LOADING BELONGS TO StreetStream NOW, not to this component.
  //
  // This used to fetch every building in the world at mount and keep them
  // forever. That is the right instinct — placed buildings are part of the city,
  // not part of the editor — and the wrong scope: the number of buildings in the
  // world is unbounded by design ("infinite suburbs, one downtown"), while the
  // number this can draw is not. StreetStream asks by cell and writes the same
  // store slice, so this still renders everything it is given; it just no longer
  // decides what that is.

  // Index the model's meshes by name once, so a paste is a lookup rather than a
  // traverse of a few thousand nodes.
  const byName = useMemo(() => {
    const m = new Map();
    city.traverse((o) => { if (o.isMesh && o.name) m.set(o.name, o); });
    return m;
  }, [city]);

  const mine = objects.filter((o) => o.zone_id === 'street' && o.kind === 'building');

  const clones = useMemo(() => (!fit ? [] : mine.map((o) => {
    const src = byName.get(o.ref);
    if (!src) {
      // Fail loudly. A name that doesn't resolve renders nothing, and silence
      // makes that indistinguishable from a broken save.
      console.warn(`[blocks] no mesh named "${o.ref}" in the city model — nothing to render`);
      return null;
    }

    // APPLY THE CITY'S FIT BY HAND.
    //
    // The obvious move — read the source's world matrix, which "already has the
    // fit folded in" — is wrong here, and quietly so. CityModel renders
    // scene.clone(true); the gltf scene this component reads from useGLTF is NOT
    // in the render tree, so every world matrix in it is IDENTITY. Decomposing
    // one yields position 0 and scale 1, which is indistinguishable from a
    // correct answer and drops the clone at raw model scale — about 30x too
    // large and thousands of units away. Pasting appeared to do nothing because
    // the building was always off past the horizon.
    //
    // So the numbers have to come from CityModel itself (store: cityFit), and
    // the source's transform is read in MODEL space and scaled up by it.
    src.updateWorldMatrix(true, false);      // model-space, parent chain applied
    const mp = new THREE.Vector3();
    const mq = new THREE.Quaternion();
    const ms = new THREE.Vector3();
    src.matrixWorld.decompose(mp, mq, ms);

    const c = src.clone(true);
    c.quaternion.copy(mq);
    c.scale.copy(ms).multiplyScalar(fit.scale);
    c.visible = true;

    // The geometry is also not centred on its own origin (the same thing that
    // put skyline chunks on the map). Offset so the building lands CENTRED on
    // its cell and RESTING on the ground, rather than half-buried and adrift.
    // Every offset is a model-space distance, so it scales by the fit too.
    const box = new THREE.Box3().setFromObject(src);
    const centre = box.getCenter(new THREE.Vector3());
    c.position.set(
      (mp.x - centre.x) * fit.scale,
      (mp.y - box.min.y) * fit.scale,
      (mp.z - centre.z) * fit.scale,
    );

    return { key: o.object_id, obj: c, o };
  }).filter(Boolean)), [mine, byName, fit]);

  useEffect(() => {
    if (mine.length) console.log(`[blocks] ${clones.length}/${mine.length} placed building(s) rendering${fit ? '' : ' — waiting on the city fit'}`);
  }, [mine.length, clones.length]);

  if (!clones.length) return null;

  return (
    <group>
      {clones.map(({ key, obj, o }) => (
        <group key={key} position={o.pos} rotation={[0, o.rotY || 0, 0]} scale={o.size || 1}>
          <primitive object={obj} />
          {/* Selection marker. Drawn through everything (depthTest off) because
              the thing you are trying to find is, by definition, the one you
              cannot currently see well enough to edit. */}
          {sel === o.object_id && (
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.08, 0]} renderOrder={999}>
              <ringGeometry args={[2.4, 3.2, 32]} />
              <meshBasicMaterial color="#ffb25e" transparent opacity={0.85} depthTest={false} depthWrite={false} fog={false} />
            </mesh>
          )}
        </group>
      ))}
    </group>
  );
}
